import type { AppNode, EdgeData } from "@features/builder/graph/types"
import { nodesGroupMapper } from "@features/builder/helpers/nodesGroupMapper"
import { TrendingFlat } from "@mui/icons-material"
import {
  Paper,
  type SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import { type Edge, useEdges, useNodes, useReactFlow } from "@xyflow/react"
import { useMemo } from "react"

export const EdgeErrors = () => {
  const edges = useEdges<Edge<EdgeData>>()
  const nodes = useNodes<AppNode>()
  const { fitView, getEdge, getInternalNode, setCenter } = useReactFlow<AppNode, Edge<EdgeData>>()
  const invalidEdges = edges.filter((edge) => edge.label)
  const mapper = useMemo(() => nodesGroupMapper(nodes), [nodes])

  const handleNodeClick = (nodeId: string) => {
    fitView({ nodes: [{ id: nodeId }], duration: 0, maxZoom: 2.5 })
  }

  const handleEdgeClick = (edgeId: string) => {
    const edge = getEdge(edgeId)
    if (!edge) return

    const sourceNode = getInternalNode(edge.data?.finalSource ?? "")
    const targetNode = getInternalNode(edge.data?.finalTarget ?? "")
    if (!sourceNode || !targetNode) return

    const sourceNodePosition = sourceNode.internals.positionAbsolute
    const targetNodePosition = targetNode.internals.positionAbsolute

    const centerX = (sourceNodePosition.x + targetNodePosition.x) / 2
    const centerY = (sourceNodePosition.y + targetNodePosition.y) / 2

    setCenter(centerX, centerY, { zoom: 2.5 })
  }

  if (invalidEdges.length === 0) return null

  return (
    <TableContainer
      component={Paper}
      sx={{ maxHeight: 250, ".MuiTableCell-root": { p: "0 10px" } }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>From</strong>
            </TableCell>
            <TableCell>
              <TrendingFlat sx={{ verticalAlign: "bottom" }} />
            </TableCell>
            <TableCell>
              <strong>To</strong>
            </TableCell>
            <TableCell>
              <strong>Error</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invalidEdges.map((edge) => {
            const sourceId = edge.data?.finalSource ?? ""
            const targetId = edge.data?.finalTarget ?? ""

            return (
              <TableRow key={edge.id}>
                <TableCell sx={clickableCellStyle} onClick={() => handleNodeClick(sourceId)}>
                  {mapper.nodesMap.get(sourceId)?.data.label}
                </TableCell>
                <TableCell sx={clickableCellStyle} onClick={() => handleEdgeClick(edge.id)}>
                  <TrendingFlat sx={{ verticalAlign: "bottom" }} />
                </TableCell>
                <TableCell sx={clickableCellStyle} onClick={() => handleNodeClick(targetId)}>
                  {mapper.nodesMap.get(targetId)?.data.label}
                </TableCell>
                <TableCell>{edge.label}</TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const clickableCellStyle: SxProps = {
  cursor: "pointer",
  "&:hover": { textDecoration: "underline" },
}
